import { isNil, flatten, values, groupBy, map } from 'lodash';
import { frameAlignStartTimeMs, frameAlignStopTimeMs } from '@utils';
import {
  GroupedBoundingPoly,
  BoundingPolyTree,
  Searcher,
} from './boundingPolyTree';
import { UDRsPolyAssetGroup } from '@common-modules/mediaDetails/models';

// build collection
//  * @param ps - array of BoundingPolys
export function buildTimelineCollection(
  ps: ReadonlyArray<GroupedBoundingPoly>,
  start: number,
  end: number,
  binsPerSecond: number,
  version: number,
  fps: number | undefined
): BoundingPolyTree | undefined {
  if (ps.length === 0) {
    return undefined;
  }

  const collection: BoundingPolyTree = {
    version,
    bins: {},
    start,
    end,
    binsPerSecond,
    groups: {},
    fps,
  };

  ps.forEach((item) => {
    const groupId = item.groupId;
    if (groupId) {
      for (
        let j = Math.floor((binsPerSecond * item.startTimeMs) / 1000);
        j < Math.ceil((binsPerSecond * item.stopTimeMs) / 1000);
        j++
      ) {
        const bin = collection.bins[j];
        if (bin !== undefined) {
          bin.push(item);
        } else {
          // create bin if nothing in it
          collection.bins[j] = [item] as GroupedBoundingPoly[];
        }

        // update groups
        const group = collection.groups[groupId];
        if (group) {
          group.push(item);
        } else {
          collection.groups[groupId] = [item];
        }
      }
    }
  });

  return collection;
}

// search for bounding polys at desired time position (seconds)
export const search: Searcher = (
  collection,
  position,
  isRect,
  previousPosition
) => {
  if (collection === undefined || position / 1000.0 > collection.end) {
    return [] as Array<GroupedBoundingPoly>;
  }

  const binIndex = Math.floor((position / 1000.0) * collection.binsPerSecond);
  const polysInBin =
    collection.bins[binIndex] || ([] as Array<GroupedBoundingPoly>);

  const polysAtCurrentTimeByGroupId = groupBy(polysInBin, 'groupId');

  // confirm if poly should be displayed on current frame / at currentTime
  let polySeries: GroupedBoundingPoly[];

  const fps = collection.fps;

  if (fps) {
    // if fps defined use exact frame boundaries to determine if poly should be displayed at currentTime
    polySeries = map(polysAtCurrentTimeByGroupId, (polys) =>
      polys.find(
        (poly) =>
          position >= frameAlignStartTimeMs(poly.startTimeMs, fps) &&
          position < frameAlignStopTimeMs(poly.stopTimeMs, fps)
      )
    ).filter((poly): poly is GroupedBoundingPoly => !!poly);
  } else {
    // this would only apply to TDOs before fall 2020, before videoFrameRate was saved
    // note: rendering of udrs may not match actual output redacted video because udr start and stop times are not aligned with actual frame boundaries!
    polySeries = map(polysAtCurrentTimeByGroupId, (polys) =>
      polys.find(
        (poly) => position >= poly.startTimeMs && position <= poly.stopTimeMs
      )
    ).filter((poly): poly is GroupedBoundingPoly => !!poly);
  }

  // optional params help draw udr timeline - consider moving this into the timeline module instead later
  if (!polySeries?.length && previousPosition && !isRect) {
    return search(collection, previousPosition);
  }

  return polySeries;
};

/**
 * Accumulate into `out` the group ids with a poly on screen at any point in
 * [fromMs, toMs].
 *
 * `search` above answers a *point* query: it takes the bin covering the
 * position, then keeps only the polys whose frame-aligned range contains that
 * exact position. That second step is where a short segment goes missing — the
 * playhead in the store advances in `timeupdate` steps (~250ms), not per frame,
 * so a segment narrower than one step can fall between two samples and never be
 * reported at all. Asking about the interval rather than the instant closes that
 * gap on the bin layout that already exists (VE-25876).
 *
 * A strict SUPERSET of `search`, not an exact mirror — deliberately, since every
 * divergence over-reports and this feature must not hide an object from the
 * operator. Two sources: the one-bin low-side margin below (which finds a poly
 * whose frame-aligned start fell into the preceding bin, where `search`'s
 * single-bin lookup would miss it), and the window itself. Do not treat this as a
 * drop-in replacement for the point query.
 *
 * When `fromMs === toMs` it reduces to `search`'s own per-branch bounds, so a
 * stationary playhead — paused, or just seeked — reports the same set.
 *
 * Accumulates into a caller-supplied set so folding several collections together
 * costs no intermediate arrays.
 */
export const collectGroupIdsInWindow = (
  collection: BoundingPolyTree | undefined,
  fromMs: number,
  toMs: number,
  out: Set<string>
): void => {
  if (collection === undefined) {
    return;
  }

  const start = Math.min(fromMs, toMs);
  const stop = Math.max(fromMs, toMs);
  if (start / 1000.0 > collection.end) {
    return;
  }

  const { fps, binsPerSecond } = collection;
  // One bin of margin on the low side. Bins are indexed off the raw
  // `startTimeMs`, while the test below uses the frame-aligned start, which can
  // land a sub-frame earlier and so belong to the preceding bin. The overlap
  // test makes the extra bin free - it can only reject.
  const firstBin = Math.floor((start / 1000.0) * binsPerSecond) - 1;
  // Clamped to the collection's own extent. `start` is bounds-checked above but
  // `stop` was not, so the loop length was a function of the caller's argument
  // rather than of the data: a large `toMs` would walk billions of empty bins on
  // the main thread. Today's only caller is bounded by SAMPLE_WINDOW_BACKSTOP_MS,
  // which is why this is unreachable rather than a live bug — but the bound
  // belongs here, where the loop is, not in the caller.
  const lastBin = Math.min(
    Math.floor((stop / 1000.0) * binsPerSecond),
    Math.floor(collection.end * binsPerSecond)
  );

  for (let bin = firstBin; bin <= lastBin; bin++) {
    const polys = collection.bins[bin];
    if (!polys) {
      continue;
    }
    for (const poly of polys) {
      const groupId = poly.groupId;
      if (!groupId || out.has(groupId)) {
        continue;
      }
      // Follows `search`'s own bounds per branch: the frame-aligned path is
      // exclusive at the stop, the legacy no-fps path is inclusive. Getting this
      // wrong under-reports a poly sitting exactly on its stop time, which on
      // pre-fall-2020 TDOs (the only ones without a frame rate) would hide it
      // from the operator.
      const polyStart = fps
        ? frameAlignStartTimeMs(poly.startTimeMs, fps)
        : poly.startTimeMs;
      const polyStop = fps
        ? frameAlignStopTimeMs(poly.stopTimeMs, fps)
        : poly.stopTimeMs;
      const overlapsStop = fps ? polyStop > start : polyStop >= start;
      if (polyStart <= stop && overlapsStop) {
        out.add(groupId);
      }
    }
  }
};

// insert items into existing BoundingPolyTree - return new boundingPolyTree with contains updated/modified bins only
function collectionInsert(
  collection: BoundingPolyTree,
  ps: ReadonlyArray<GroupedBoundingPoly>
): BoundingPolyTree | undefined {
  if (ps.length === 0) {
    return undefined;
  }

  const collectionUpdates = buildTimelineCollection(
    ps,
    collection.start,
    collection.end,
    collection.binsPerSecond,
    collection.version + 1,
    collection.fps
  );

  // now copy in values from the orig collection with overlapping bins
  if (collectionUpdates !== undefined) {
    /* eslint guard-for-in: 0 */
    for (const binIndex in collectionUpdates.bins) {
      const updatedBin = collectionUpdates.bins[binIndex];
      const origBin = collection.bins[binIndex];
      if (
        origBin !== undefined &&
        updatedBin !== undefined &&
        origBin.length > 0
      ) {
        // collectionUpdates.bins[binIndex] = updatedBin.concat(origBin); // ORIGINAL - THIS CAUSES BUG WHEN LIVE-TRACKING
        collectionUpdates.bins[binIndex] = origBin.concat(updatedBin); // NEW/UPDATED UDRS MUST BE AT END FOR SOME REASON!
      }
    }
  }
  return collectionUpdates;
}

// return updated boundingPolyTree
export function updateTimelineCollection(
  collection: BoundingPolyTree,
  updatedGroups: { [groupId: string]: Array<GroupedBoundingPoly> }
): BoundingPolyTree | undefined {
  // step 1: copy current collection bins
  const updatedBins = {
    ...collection.bins,
  };

  // step 2: any group being removed or updated must first be purged from collection
  Object.keys(updatedGroups).forEach((groupId) => {
    const group = collection.groups[groupId];
    if (group) {
      group.forEach((item) => {
        for (
          let j = Math.floor(
            (collection.binsPerSecond * item.startTimeMs) / 1000
          );
          j < Math.ceil((collection.binsPerSecond * item.stopTimeMs) / 1000);
          j++
        ) {
          const purgedBin: GroupedBoundingPoly[] = [];
          const binList = updatedBins[j];
          if (!isNil(binList)) {
            binList.forEach((item) => {
              if (!(item.groupId === groupId)) {
                purgedBin.push(item);
              }
            });
          }
          updatedBins[j] = purgedBin;
        }
      });
    }
  });

  const updatedCollection = {
    ...collection,
    version: collection.version + 1,
    bins: updatedBins,
    groups: {
      ...collection.groups,
      ...updatedGroups,
    },
  };

  // step 3: insert new/modified groups
  const ps = flatten(values(updatedGroups));
  const insertionCollection = collectionInsert(updatedCollection, ps);
  if (insertionCollection) {
    updatedCollection.bins = {
      ...updatedCollection.bins,
      ...insertionCollection.bins,
    };
  }
  return updatedCollection;
}

// return updated boundingPolyTree - original version require on providing previous group info
// note this was only made to work for UDRs - keep for now as a reference
export function updateTimelineCollectionV1(
  collection: BoundingPolyTree,
  ps: ReadonlyArray<GroupedBoundingPoly>,
  purgeGroups: { [groupId: string]: UDRsPolyAssetGroup }
): BoundingPolyTree | undefined {
  // step 1: copy current collection bins
  const updatedBins = {
    ...collection.bins,
  };

  // step 2: any group being removed or updated must first be purged from collection
  for (const [groupId, groupInfo] of Object.entries(purgeGroups)) {
    groupInfo.series.forEach((item) => {
      for (
        let j = Math.floor(
          (collection.binsPerSecond * item.startTimeMs) / 1000
        );
        j < Math.ceil((collection.binsPerSecond * item.stopTimeMs) / 1000);
        j++
      ) {
        const purgedBin: GroupedBoundingPoly[] = [];
        const binList = updatedBins[j];
        if (!isNil(binList)) {
          binList.forEach((item) => {
            if (item.groupId !== groupId) {
              purgedBin.push(item);
            }
          });
        }
        updatedBins[j] = purgedBin;
      }
    });
  }

  const updatedCollection = {
    ...collection,
    version: collection.version + 1,
    bins: updatedBins,
  };

  // step 3: insert new/modified udrs
  const insertionCollection = collectionInsert(updatedCollection, ps);
  if (insertionCollection) {
    updatedCollection.bins = {
      ...updatedCollection.bins,
      ...insertionCollection.bins,
    };
  }

  return updatedCollection;
}
